import NextImage from 'next/image';
import { useId } from 'react';
import { cn } from 'lib/utils';
import { type ImageField } from 'lib/types';
import {
  type BloomProps,
  Bloom,
  type HeaderSectionProps,
  HeaderSection,
  Wrapper,
} from 'ui';

export type AwardsProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  headerAlignment?: HeaderSectionProps['alignment'];
  awards: {
    year: string;
    title: string;
    logo: ImageField;
  }[];
  bloom?: BloomProps;
};

const getColumnSpan = (
  index: number,
  totalCards: number,
  maxCardsPerRow: number
) => {
  // If we have fewer cards than max per row, they should fill the entire row
  if (totalCards <= maxCardsPerRow) {
    return 12 / totalCards;
  }

  // For more cards than max per row
  const fullRows = Math.floor(totalCards / maxCardsPerRow);
  const remainingCards = totalCards % maxCardsPerRow;
  const currentRow = Math.floor(index / maxCardsPerRow);

  // If we're in a full row (not the last row)
  if (currentRow < fullRows) {
    return 12 / maxCardsPerRow;
  }

  // If we're in the last row and there are remaining cards
  if (remainingCards > 0) {
    return 12 / remainingCards;
  }

  // Default
  return 12 / maxCardsPerRow;
};

const Awards = ({
  className,
  eyebrow,
  title,
  intro,
  headerAlignment,
  awards,
  bloom,
}: AwardsProps) => {
  const componentId = useId();

  if (!awards?.length || awards.length < 2) return null;

  return (
    <Wrapper
      as="section"
      variant="page-full"
      className={cn('relative flex flex-col gap-12 lg:gap-18', className)}
    >
      <Bloom {...bloom} />

      <HeaderSection
        content={intro}
        alignment={headerAlignment}
        {...{ eyebrow, title }}
        className="relative z-2"
      />

      <div
        className="relative z-2 grid grid-cols-1 xs:grid-cols-12 gap-8 lg:gap-10"
        role="list"
        aria-label={`${awards.length} awards displayed`}
      >
        {awards.slice(0, 8).map((award, index) => {
          // Calculate spans for different breakpoints
          const xsColSpan = getColumnSpan(index, awards.length, 2); // xs: 2 per row
          const mdColSpan = getColumnSpan(index, awards.length, 3); // md: 3 per row
          const xlColSpan = getColumnSpan(index, awards.length, 4); // xl: 4 per row

          return (
            <article
              key={index}
              role="listitem"
              className={cn(
                'flex flex-col bg-clay-50 text-graphite-900 rounded-lg p-8 lg:h-69',
                // xs breakpoint - 2 cards per row
                {
                  'xs:col-span-1': xsColSpan === 1,
                  'xs:col-span-2': xsColSpan === 2,
                  'xs:col-span-3': xsColSpan === 3,
                  'xs:col-span-4': xsColSpan === 4,
                  'xs:col-span-6': xsColSpan === 6,
                  'xs:col-span-12': xsColSpan === 12,
                },
                // md breakpoint - 3 cards per row
                {
                  'md:col-span-1': mdColSpan === 1,
                  'md:col-span-2': mdColSpan === 2,
                  'md:col-span-3': mdColSpan === 3,
                  'md:col-span-4': mdColSpan === 4,
                  'md:col-span-6': mdColSpan === 6,
                  'md:col-span-12': mdColSpan === 12,
                },
                // xl breakpoint - 4 cards per row
                {
                  'xl:col-span-1': xlColSpan === 1,
                  'xl:col-span-2': xlColSpan === 2,
                  'xl:col-span-3': xlColSpan === 3,
                  'xl:col-span-4': xlColSpan === 4,
                  'xl:col-span-6': xlColSpan === 6,
                  'xl:col-span-12': xlColSpan === 12,
                }
              )}
              aria-labelledby={`award-title-${componentId}-${index}`}
              aria-describedby={`award-year-${componentId}-${index}`}
            >
              <header className="grow flex flex-col">
                <p
                  id={`award-year-${componentId}-${index}`}
                  className="text-p1 font-bold"
                  aria-label={`Award year: ${award.year}`}
                >
                  {award.year}
                </p>
                <p
                  id={`award-title-${componentId}-${index}`}
                  className="text-p1 line-clamp-2"
                >
                  {award.title}
                </p>
              </header>

              <footer className="mt-5 pt-5 border-t border-clay-200">
                <NextImage
                  src={award.logo.src}
                  alt={award.logo.alt || `Logo for ${award.title} award`}
                  width={award.logo.width}
                  height={award.logo.height}
                  className="max-h-10 w-auto object-contain"
                  role="img"
                  aria-label={
                    award.logo.alt ||
                    `Organization logo for ${award.title} award`
                  }
                />
              </footer>
            </article>
          );
        })}
      </div>
    </Wrapper>
  );
};

export default Awards;
